import {
  ReadOnlyProvider,
  SyncFormChanges,
  createForm,
  useFormContext,
} from "modules/form";
import React, { useContext, useMemo, useRef } from "react";
import Fields from "components/ui/Fields";
import { useTranslation } from "react-i18next";
import Validator from "services/validator";
import {
  All,
  ApplyIf,
  EmptyString,
  MaxLength,
  Missing,
  SemanticVersion,
  isValidIP,
  isValidIPRange,
} from "services/validator/rules";
import i18next from "i18next";
import { useDispatch } from "react-redux";
import { ProfileBuilderContext } from "modules/profileBuilder/services";
import createFormActions from "modules/form/actions";
import styled, { css } from "styled-components";
import store from "services/store";
import { InfoBanner } from "./Variables";
import Button, { TextButton } from "components/ui/Button";
import Icon from "components/ui/Icon";
import {
  faEdit,
  faPlusCircle,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";
import Modal from "components/ui/Modal";
import ModalService from "services/modal";
import {
  backgroundSubtleDangerBold,
  borderDefault,
  colorIconDefault,
  colorIconSubtle,
  elevationSurfaceSubtlest,
  textSubtle,
} from "utils/constants/colors";
import { FieldLabel } from "components/styled/Field";
import Ellipsis from "components/common/Ellipsis";
import ConfirmationModal from "components/common/ConfirmationModal";
import { MASKED_VARIABLE_DEFAULT_VALUE } from "utils/constants";
import { IsTextInput } from "modules/profileBuilder/actions/createProfileForm";

const FormWrapper = styled.div`
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;

  .ant-alert {
    margin: 0;
  }
`;
const FieldsWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  padding: 0 24px;
`;

const DropdownOptionsField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  border-radius: 4px;
  padding: 12px;
  border: 1px solid ${borderDefault};
`;

const OptionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
`;

const DropdownOptionCard = styled.div`
  width: 100%;
  background: ${elevationSurfaceSubtlest};
  padding: 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props) =>
    props.withError &&
    css`
      background: ${backgroundSubtleDangerBold};
    `}
`;

const OptionDetails = styled.div`
  color: ${textSubtle};
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-grow: 1;
  overflow: hidden;
`;

const OptionLabel = styled(Ellipsis)`
  font-size: 16px;
`;

const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;

  & ${TextButton} {
    color: ${colorIconDefault};

    :disabled {
      color: ${colorIconSubtle};
    }
  }
`;

export const variablePreviewValidator = new Validator();
variablePreviewValidator.addRule(
  "preview",
  ApplyIf((value, key, data) => data.isRequired, Missing())
);
variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(IsTextInput(), (value, key, data) => value && data.useRegex),
    (value, key, data) => {
      try {
        return !new RegExp(data.regex).test(value)
          ? i18next.t(`Value does not match the regex pattern "${data.regex}"`)
          : false;
      } catch (e) {
        return false;
      }
    }
  )
);

variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(IsTextInput(), (value, key, data) => value && data.format === "number"),
    (value, key, data) => {
      return isNaN(value) ? i18next.t("Value is not a number") : false;
    }
  )
);

variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(
      IsTextInput(),
      (value, key, data) => value && data.format === "version"
    ),
    SemanticVersion()
  )
);

variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(
      IsTextInput(),
      (value, key, data) => value && data.format === "boolean"
    ),
    (value, key, data) => {
      return ![true, false].includes(value)
        ? i18next.t("Value is not a boolean")
        : false;
    }
  )
);

variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(
      IsTextInput(),
      (value, key, data) => value && ["ipv4", "ipv6"].includes(data.format)
    ),
    (value, key, data) =>
      isValidIP({ version: data.format.replace("ipv", "") })(value, key, data)
  )
);
variablePreviewValidator.addRule(
  "preview",
  ApplyIf(
    All(
      IsTextInput(),
      (value, key, data) => value && data.format === "ipv4cidr"
    ),
    (value, key, data) =>
      isValidIPRange({
        version: data.format.replace("ipv", "").replace("cidr", ""),
      })(value, key, data)
  )
);

export function getPreviewValue(data) {
  let previewValue = data.defaultValue || "";
  if (!data.useDefaultValue) {
    previewValue = "";
  }
  if (
    data.format === "boolean" &&
    data.useDefaultValue &&
    data.inputType !== "dropdown"
  ) {
    previewValue =
      data.defaultValue === "true"
        ? true
        : data.defaultValue === "false"
        ? false
        : null;
  }

  return previewValue;
}

export const previewActions = createFormActions({
  validator: variablePreviewValidator,
  async init() {
    const fieldData = store.getState().forms["profile-variable"].data;
    return {
      ...fieldData,
      preview: getPreviewValue(fieldData),
    };
  },
});

const PreviewFieldWrapper = styled.div`
  margin-top: 24px;
`;
const PreviewWrap = styled.div`
  background-color: #f7f9ff;
  border: 1px solid #dee1ea;
  padding: 8px;
  margin-bottom: 20px;

  > div {
    margin: 0;
  }
`;

export function PreviewVariableField({ data, name }) {
  const { isProfileAssociatedToTemplate } =
    useContext(ProfileBuilderContext)?.state || {};
  let PreviewComponent = data.isMasked ? Fields.PasswordField : Fields.Input;

  if (data.isHidden) {
    PreviewComponent = Fields.Empty;
  }

  if (data.format === "boolean") {
    PreviewComponent = Fields.Switch;
  }

  if (data.inputType === "dropdown" && !data.isHidden) {
    PreviewComponent = Fields.Select;
  }

  return (
    <ReadOnlyProvider
      isActive={data.isReadOnly || isProfileAssociatedToTemplate}
    >
      <PreviewComponent
        name={name}
        data-qa={name}
        label={data.displayName}
        helperText={data.description}
        required={data.isRequired}
        {...(data.inputType === "dropdown" && {
          options: data.options.map((option) => ({
            ...option,
            value: option.label,
          })),
        })}
      />
    </ReadOnlyProvider>
  );
}

const PreviewForm = createForm({
  actions: previewActions,
  Component({ data }) {
    const { t } = useTranslation();
    return (
      <PreviewFieldWrapper>
        <Fields.Label label={t("Preview")} required={true} />
        <PreviewWrap>
          <PreviewVariableField name="preview" data={data} />
        </PreviewWrap>
      </PreviewFieldWrapper>
    );
  },
});

const variableOptionModal = new ModalService("variable-dropdown-option");
const formatConfirmationModal = new ModalService(
  "variable-format-confirmation"
);

export const variableOptionValidator = new Validator();
variableOptionValidator.addRule("value", Missing());
variableOptionValidator.addRule(
  "value",
  EmptyString({
    message: () =>
      i18next.t("Values containing only whitespace are not allowed"),
  })
);

variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) => value && isNotMasked(value, key, data),
    (value, key, data) => {
      const selectedIndex = variableOptionModal.data?.index;

      const duplicates = (data.options || []).find(
        (option, index) =>
          option.value?.trim() === value?.trim() && index !== selectedIndex
      );

      if (duplicates) {
        return i18next.t("Values must be unique");
      }
      return false;
    }
  )
);

const isNotMasked = (value, key, data) =>
  data.isOptionSaved ? value !== MASKED_VARIABLE_DEFAULT_VALUE : true;

variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) =>
      value && data.format === "number" && isNotMasked(value, key, data),
    (value, key, data) => {
      return isNaN(value) ? i18next.t("Value is not a number") : false;
    }
  )
);

variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) =>
      value && data.format === "version" && isNotMasked(value, key, data),
    SemanticVersion()
  )
);

variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) =>
      value && data.format === "boolean" && isNotMasked(value, key, data),
    (value, key, data) => {
      return !["true", "false"].includes(value)
        ? i18next.t("Value is not a boolean")
        : false;
    }
  )
);

variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) =>
      value &&
      ["ipv4", "ipv6"].includes(data.format) &&
      isNotMasked(value, key, data),
    (value, key, data) =>
      isValidIP({ version: data.format.replace("ipv", "") })(value, key, data)
  )
);
variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) =>
      value && data.format === "ipv4cidr" && isNotMasked(value, key, data),
    (value, key, data) =>
      isValidIPRange({
        version: data.format.replace("ipv", "").replace("cidr", ""),
      })(value, key, data)
  )
);
variableOptionValidator.addRule(
  "value",
  ApplyIf(
    (value, key, data) => {
      return (
        value && data.useRegex && data.regex && isNotMasked(value, key, data)
      );
    },
    (value, key, data) => {
      try {
        return new RegExp(data.regex).test(value)
          ? false
          : i18next.t(`Value does not match the regex pattern "${data.regex}"`);
      } catch (e) {
        return false;
      }
    }
  )
);
variableOptionValidator.addRule(
  "label",
  ApplyIf((value, key, data) => data.isMasked, Missing())
);
variableOptionValidator.addRule(
  "label",
  EmptyString({
    message: () =>
      i18next.t("Labels containing only whitespace are not allowed"),
  })
);
variableOptionValidator.addRule("label", MaxLength(64));
variableOptionValidator.addRule("label", (value, key, data) => {
  const selectedIndex = variableOptionModal.data?.index;
  const duplicates = (data.options || []).find(
    (option, index) =>
      option.label?.trim() === value?.trim() && index !== selectedIndex
  );

  if (duplicates) {
    return i18next.t("Labels must be unique");
  }
  return false;
});
variableOptionValidator.addRule("description", MaxLength(255));

export const variableOptionActions = createFormActions({
  validator: variableOptionValidator,
  async init() {
    const fieldData = store.getState().forms["profile-variable"].data;
    const initialData = store.getState().forms["profile-variable"]?.initialData;
    const modalData = variableOptionModal.data;
    const option = fieldData.options[modalData?.index] || {};
    // Check if the option being edited was previously saved, needed for validation
    const isOptionSaved = initialData.options?.find(
      (initialOption) => initialOption.label === option.label
    );

    return {
      ...fieldData,
      value: option?.value || "",
      label: option?.label || "",
      description: option?.description || "",
      isOptionSaved,
    };
  },
  async submit(data) {
    const label = data.label || data.value;
    return {
      value: data.value?.trim(),
      label: label?.trim()?.slice(0, 64),
      description: data.description?.trim(),
    };
  },
});

const VariableOptionForm = createForm({
  actions: variableOptionActions,
  Component() {
    const { t } = useTranslation();
    const form = useFormContext();
    const modalData = variableOptionModal.data;
    const canEditLabel =
      modalData?.index !== undefined ? modalData?.canEditLabel : true;
    const isMasked = form?.data?.isMasked;

    return (
      <PreviewFieldWrapper>
        <Fields.Input
          label={t("Value")}
          name="value"
          data-qa="value"
          description={t("The value inserted into the YAML file.")}
        />
        <Fields.Input
          label={t("Label")}
          name="label"
          data-qa="label"
          description={t("Text displayed in the dropdown field.")}
          helperText={t(
            "Defaults to Value if left empty. Up to 64 characters allowed."
          )}
          disabled={!canEditLabel}
          required={isMasked}
        />
        <Fields.TextArea
          label={t("Description")}
          name="description"
          data-qa="description"
          description={t(
            "Additional info shown as a tooltip next to this option in the dropdown."
          )}
          required={false}
        />
      </PreviewFieldWrapper>
    );
  },
});

function onAddVariableDropdownOption({ formModule }) {
  return async (dispatch) => {
    variableOptionModal.open({}).then(async () => {
      const data = await store.dispatch(
        variableOptionActions.submit({ module: "variableOption" })
      );

      const currentOptions = formModule.data.options || [];
      formModule.onFieldChange("options", [...currentOptions, data]);
      formModule.onFieldChange("defaultValue", "");
      formModule.validateField("options");
    });
    dispatch(variableOptionActions.init({ module: "variableOption" }));
  };
}

function onEditVariableDropdownOption({
  formModule,
  index,
  canEditLabel = false,
}) {
  return async (dispatch) => {
    variableOptionModal.open({ index, canEditLabel }).then(async () => {
      const data = await store.dispatch(
        variableOptionActions.submit({ module: "variableOption" })
      );

      const currentOptions = [...formModule.data.options] || [];
      const option = currentOptions[index];

      currentOptions.splice(index, 1, { ...option, ...data });
      formModule.onFieldChange("options", currentOptions);
      formModule.onFieldChange("defaultValue", "");
      formModule.validateField("options");
    });
    dispatch(variableOptionActions.init({ module: "variableOption" }));
  };
}

function onDeleteVariableDropdownOption({ formModule, index }) {
  return () => {
    const currentOptions = [...formModule.data.options] || [];
    currentOptions.splice(index, 1);
    formModule.onFieldChange("options", currentOptions);
    formModule.onFieldChange("defaultValue", "");
  };
}

function VariableOptionModal() {
  const { t } = useTranslation();
  const title = variableOptionModal.data?.index
    ? t("Edit dropdown option")
    : t("Add dropdown option");

  return (
    <Modal title={title} service={variableOptionModal}>
      <VariableOptionForm module="variableOption" />
    </Modal>
  );
}

function OptionCard({
  option,
  onEdit,
  onDelete,
  viewOnly = false,
  withError = false,
}) {
  return (
    <DropdownOptionCard withError={withError}>
      <OptionDetails>
        <OptionLabel title={option.label}>{option.label}</OptionLabel>
        <Ellipsis title={option.value}>Value: {option.value}</Ellipsis>
        {option.description ? (
          <Ellipsis title={option.description}>
            Description: {option.description}
          </Ellipsis>
        ) : null}
      </OptionDetails>
      <ActionWrapper>
        <TextButton
          data-qa="edit-dropdown-option"
          onClick={onEdit}
          disabled={viewOnly}
        >
          <Icon awesome={faEdit} />
        </TextButton>
        <TextButton
          data-qa="delete-dropdown-option"
          onClick={onDelete}
          disabled={viewOnly}
        >
          <Icon awesome={faTrashAlt} />
        </TextButton>
      </ActionWrapper>
    </DropdownOptionCard>
  );
}

export function VariableDetailsForm({
  onChange = () => {},
  inUse,
  actions = {},
  preview = true,
}) {
  const { t } = useTranslation();
  const VariableForm = useMemo(() => {
    if (!actions) {
      return () => null;
    }

    return createForm({
      actions,
      Component: () => {
        const form = useFormContext();
        const { t } = useTranslation();
        const dispatch = useDispatch();
        const { fieldErrors = {}, initialData } = form;
        const optionsErrors = fieldErrors?.["options"];
        const invalidDropdownOptions = optionsErrors?.[0]?.invalidOptions || [];
        const initialOptions = initialData?.options || [];

        const renderDropdownOptions = () => {
          if (form.data.inputType !== "dropdown") {
            return null;
          }

          return (
            <>
              <FieldLabel>{t("Dropdown options")}</FieldLabel>
              <DropdownOptionsField>
                {form.data.options.length ? (
                  <OptionsWrap>
                    {form.data.options.map((option, index) => {
                      const initialOption = initialOptions.find(
                        (initialOption) => initialOption.label === option.label
                      );
                      const canEditLabel =
                        initialOption?.value !== MASKED_VARIABLE_DEFAULT_VALUE;

                      return (
                        <OptionCard
                          key={index}
                          option={option}
                          onEdit={() => {
                            dispatch(
                              onEditVariableDropdownOption({
                                formModule: form,
                                index,
                                canEditLabel,
                              })
                            );
                          }}
                          onDelete={() => {
                            dispatch(
                              onDeleteVariableDropdownOption({
                                formModule: form,
                                index,
                              })
                            );
                          }}
                          viewOnly={inUse}
                          withError={invalidDropdownOptions.includes(
                            option.value
                          )}
                        />
                      );
                    })}
                  </OptionsWrap>
                ) : null}
                <Button
                  data-qa="add-variable-dropdown-option"
                  secondary
                  onClick={() => {
                    dispatch(
                      onAddVariableDropdownOption({
                        formModule: form,
                      })
                    );
                  }}
                  disabled={inUse}
                >
                  <Icon awesome={faPlusCircle} />
                  {t("Add dropdown option")}
                </Button>
              </DropdownOptionsField>
            </>
          );
        };

        const resetRegexFields = (value) => {
          if (["string", "number"].includes(value)) {
            return;
          }
          form.onFieldChange("regex", "");
          form.onFieldChange("useRegex", false);
          form.revalidateField("regex");
        };

        return (
          <>
            <ReadOnlyProvider isActive={inUse}>
              <Fields.Input
                addonBefore={"{{.spectro.var."}
                addonAfter={"}}"}
                label={t("Variable")}
                name="name"
                data-qa="name"
                disabled={form.data.isPersisted}
              />
              <Fields.Input
                label={t("Display name")}
                name="displayName"
                description={t(
                  "Name for the profile variable that is visible on cluster creation"
                )}
                data-qa="displayName"
              />
              <Fields.TextArea
                label={t("Description")}
                name="description"
                data-qa="description"
                required={false}
              />
              <Fields.Select
                label={t("Input type")}
                name="inputType"
                data-qa="inputType"
                options={[
                  { label: t("Text"), value: "text" },
                  { label: t("Dropdown"), value: "dropdown" },
                ]}
                onChange={(value) => {
                  form.onFieldChange("inputType", value);
                  form.onFieldChange("defaultValue", "");
                  form.revalidateField("options");
                }}
              />
              <Fields.Select
                label={t("Format")}
                name="format"
                data-qa="format"
                options={[
                  { label: t("String"), value: "string" },
                  { label: t("Number"), value: "number" },
                  { label: t("Boolean"), value: "boolean" },
                  {
                    label: t("Version"),
                    value: "version",
                    description: t(
                      "Format follows the Semantic Versioning convention (e.g. 1.0.0)"
                    ),
                  },
                  { label: t("IPv4"), value: "ipv4" },
                  { label: t("IPv4 CIDR"), value: "ipv4cidr" },
                  { label: t("IPv6"), value: "ipv6" },
                ]}
                onChange={(value) => {
                  if (
                    form.data.inputType === "dropdown" &&
                    form.data.options.length
                  ) {
                    return formatConfirmationModal.open().then(() => {
                      form.onFieldChange("options", []);
                      form.onFieldChange("defaultValue", "");
                      form.onFieldChange("format", value);
                      resetRegexFields(value);
                    });
                  }

                  form.onFieldChange("format", value);
                  resetRegexFields(value);
                }}
              />
              {renderDropdownOptions()}
              <Fields.Error name="options" />
              <Fields.Switch
                label={t("Custom input validation")}
                name="useRegex"
                data-qa="useRegex"
                required={false}
                description={t(
                  "If enabled this variable's value must match with a regex pattern"
                )}
                disabled={!["string", "number"].includes(form.data.format)}
                onChange={(value) => {
                  form.onFieldChange("useRegex", value);
                  form.revalidateField("regex");
                  form.revalidateField("options");
                }}
              />
              {form.data.useRegex ? (
                <Fields.Input
                  name="regex"
                  data-qa="regex"
                  onChange={(value) => {
                    form.onFieldChange("regex", value);
                    form.validateField("options");
                  }}
                />
              ) : null}
              <Fields.Switch
                label={t("Required")}
                name="isRequired"
                required={false}
                data-qa="isRequired"
              />
              <Fields.Switch
                label={t("Default value")}
                name="useDefaultValue"
                data-qa="useDefaultValue"
                required={false}
                description={t("Predefine a value for this variable")}
                onChange={(value) => {
                  if (form.data.inputType === "dropdown" && !value) {
                    form.onFieldChange("defaultValue", "");
                  }
                  form.onFieldChange("useDefaultValue", value);
                  form.revalidateField("defaultValue");
                }}
              />
              {form.data.useDefaultValue ? (
                form.data.inputType === "dropdown" ? (
                  <Fields.Select
                    name="defaultValue"
                    data-qa="defaultValue"
                    options={form.data.options.map((option) => ({
                      ...option,
                      value: option.label,
                    }))}
                    placeholder={t("Default value")}
                  />
                ) : (
                  <Fields.Input name="defaultValue" data-qa="defaultValue" />
                )
              ) : null}
              <Fields.Switch
                label={t("Mask value")}
                name="isMasked"
                data-qa="isMasked"
                required={false}
                description={t(
                  "Perfect for passwords and other sensitive data"
                )}
              />
              <Fields.Switch
                label={t("Hidden")}
                name="isHidden"
                required={false}
                data-qa="isHidden"
                description={t(
                  "Users will not see this variable when creating a cluster with this profile"
                )}
                onChange={(value) => {
                  form.onFieldChange("isHidden", value);
                  if (
                    !form.data.isPersisted &&
                    !form.data.isReadOnlyUpdatedByUser
                  ) {
                    form.onFieldChange("isReadOnly", value);
                  }
                  form.revalidateField("useDefaultValue");
                }}
              />
              <Fields.Switch
                label={t("Read-only")}
                name="isReadOnly"
                required={false}
                data-qa="isReadOnly"
                description={t(
                  "If enabled this variable's value will not be editable when creating a cluster"
                )}
                onChange={(value) => {
                  form.onFieldChange("isReadOnly", value);
                  form.onFieldChange("isReadOnlyUpdatedByUser", true);
                  form.revalidateField("useDefaultValue");
                }}
              />
            </ReadOnlyProvider>
            <SyncFormChanges onChange={onChange.current} />
          </>
        );
      },
    });
  }, [actions, inUse, onChange]);

  return (
    <FormWrapper>
      <FieldsWrapper>
        {preview && (
          <PreviewForm module="profile-variable-preview" preventInitOnMount />
        )}
        <VariableForm module="profile-variable" preventInitOnMount />
      </FieldsWrapper>
      <InfoBanner />
      <VariableOptionModal />
      <ConfirmationModal
        type="warning"
        service={formatConfirmationModal}
        okLabel={t("Confirm")}
      >
        <ConfirmationModal.Question>
          {t(
            "Changing the format type will remove all existing dropdown options"
          )}
        </ConfirmationModal.Question>
      </ConfirmationModal>
    </FormWrapper>
  );
}

export function VariableForm() {
  const module = useContext(ProfileBuilderContext);
  const dispatch = useDispatch();
  const inUse =
    module.state.isClusterProfileUsed ||
    module.state.isProfileAssociatedToTemplate;
  const actions = module.actions.forms.variableFormActions;

  const onChange = useRef((data) => {
    dispatch(
      previewActions.batchChange({
        updates: data,
        module: "profile-variable-preview",
      })
    );

    let previewValue = getPreviewValue(data);
    dispatch(
      previewActions.onChange({
        name: "preview",
        value: previewValue,
        module: "profile-variable-preview",
      })
    );
    dispatch(
      previewActions.validateField({
        name: "preview",
        module: "profile-variable-preview",
      })
    );
  });

  return (
    <VariableDetailsForm onChange={onChange} inUse={inUse} actions={actions} />
  );
}
